import { useState } from 'react';
import { startCase } from 'lodash';
import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import CheckmarkCircleIcon from 'svg/CheckmarkCircleIcon';

import { PayerCandidate, payerOtherNames } from './eligibilityData';
import PayerOtherNamesModal from './PayerOtherNamesModal';

// One selectable payer option in the "Select Payer" grid. A radio card: click anywhere to
// select. Shows the payer logo (when Stedi has one), coverage types, operating states, programs,
// and the payer's other/alternate names (a preview + a "View N more" modal with client-side search).

// How many alternate names to preview on the card before collapsing the rest behind "View N more".
const OTHER_NAMES_PREVIEW = 5;

interface Props {
  candidate: PayerCandidate;
  selected: boolean;
  onSelect: () => void;
  // A card nobody may pick — a view-only account. Still a radio, still readable, and "View N More"
  // still opens: it reads the names the search already returned and writes nothing.
  disabled?: boolean;
}

function joinOrDash(values: string[]): string {
  return values.length > 0 ? values.join(', ') : '–';
}

export default function PayerCandidateCard({ candidate, selected, onSelect, disabled = false }: Props) {
  const [namesOpen, setNamesOpen] = useState(false);
  const otherNames = payerOtherNames(candidate);
  const preview = otherNames.slice(0, OTHER_NAMES_PREVIEW);
  const remaining = otherNames.length - preview.length;
  const payerName = candidate.displayName || candidate.primaryPayerId || 'Unknown payer';

  return (
    <>
      <Card
        role='radio'
        aria-checked={selected}
        aria-disabled={disabled}
        // aria-disabled rather than removal from the tab order: a reader on a keyboard still has to
        // be able to reach the card to read it, and the state is what tells them why it won't take.
        tabIndex={0}
        $selected={selected}
        $disabled={disabled}
        onClick={disabled ? undefined : onSelect}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
        data-testid='payer-candidate'
        data-payer-id={candidate.primaryPayerId ?? ''}>
        <Header>
          <Logo aria-hidden>{candidate.logo ? <img src={candidate.logo} alt='' /> : <LogoFallback />}</Logo>
          <Name>{payerName}</Name>
          {selected ? (
            <CheckmarkCircleIcon width={18} height={18} aria-hidden />
          ) : (
            <Radio $selected={selected} aria-hidden />
          )}
        </Header>

        <Rows>
          <Row>
            <RowLabel>Coverage Types</RowLabel>
            <RowValue>{joinOrDash(candidate.coverageTypes.map((c) => startCase(c)))}</RowValue>
          </Row>
          <Row>
            <RowLabel>Operating States</RowLabel>
            <RowValue>{joinOrDash(candidate.operatingStates)}</RowValue>
          </Row>
          <Row>
            <RowLabel>Programs</RowLabel>
            <RowValue>{joinOrDash(candidate.programs.map((p) => startCase(p.toLowerCase())))}</RowValue>
          </Row>
          <LastRow>
            <RowLabel>Other Names</RowLabel>
            <OtherNamesCell>
              {preview.length === 0 ? (
                <RowValue>–</RowValue>
              ) : (
                <NamesListWrap>
                  <OtherNames>
                    {preview.map((name) => (
                      <li key={name}>{name}</li>
                    ))}
                  </OtherNames>
                </NamesListWrap>
              )}
              {remaining > 0 && (
                <ViewMore
                  type='button'
                  data-testid='view-more-names'
                  onClick={(e) => {
                    e.stopPropagation();
                    setNamesOpen(true);
                  }}>
                  View {remaining} More
                </ViewMore>
              )}
            </OtherNamesCell>
          </LastRow>
        </Rows>
      </Card>

      {namesOpen && (
        <PayerOtherNamesModal payerName={payerName} names={otherNames} onClose={() => setNamesOpen(false)} />
      )}
    </>
  );
}

const Card = styled.div<{ $selected: boolean; $disabled: boolean }>`
  text-align: left;
  background: ${({ $selected }) => ($selected ? colors.blue5 : colors.white)};
  border: 1px solid ${({ $selected }) => ($selected ? colors.blue100 : colors.black10)};
  border-radius: 8px;
  height: 240px;
  padding: 20px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  display: flex;
  flex-direction: column;
  gap: 16px;

  /* No hover highlight on a card that can't be picked — the blue border is this grid's "click me". */
  &:hover {
    border-color: ${({ $selected, $disabled }) =>
      $disabled ? ($selected ? colors.blue100 : colors.black10) : colors.blue100};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Logo = styled.span`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 100%;
  }
`;

const LogoFallback = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: ${colors.black5};
`;

const Name = styled(BodySmallBold)`
  flex: 1;
  min-width: 0;
  font-size: 15px;
  color: ${colors.black100};
`;

const Radio = styled.span<{ $selected: boolean }>`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid ${({ $selected }) => ($selected ? colors.blue100 : colors.black25)};
  background: ${({ $selected }) =>
    $selected ? `radial-gradient(${colors.blue100} 0 5px, ${colors.white} 6px)` : colors.white};
`;

// Fills whatever vertical space the card's fixed height (240px) leaves after the header and the
// first three rows, so the last row (Other Names) can grow into it instead of the card's padding
// getting squeezed or the content overflowing past it.
const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 12px;

  /* The label column gives up what the values need: "Dental, Medical, Vision" on one line is the
     whole point of the row, and at 130px it wraps to three. */
  @media ${({ theme }) => theme.devices.mobile} {
    grid-template-columns: 104px 1fr;
  }
`;

// Other Names: the one row whose content varies in length, so it's the one that absorbs the
// card's remaining height — min-height: 0 lets it shrink below its content's natural size.
const LastRow = styled(Row)`
  flex: 1;
  min-height: 0;
`;

const RowLabel = styled(BodySmall)`
  color: ${colors.black50};
`;

const RowValue = styled(BodySmall)`
  color: ${colors.black100};
`;

// overflow: hidden lives on NamesListWrap alone, not this whole cell, so a long name list clips
// itself rather than pushing "View N More" past the card's fixed height — the link has to stay
// visible for a reviewer to reach the overflow at all.
const OtherNamesCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  min-height: 0;
`;

const NamesListWrap = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const OtherNames = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2px;

  li {
    font-size: 13px;
    line-height: 1.35;
    color: ${colors.black100};
    word-break: break-word;
  }
`;

const ViewMore = styled.button`
  align-self: flex-start;
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.blue100};

  &:hover {
    text-decoration: underline;
  }
`;
